'use client';

import { useUserProfile } from '@/modules/shared/api';
import useIsRightToLeft from '@/modules/shared/hooks/useIsRightToLeft';
import { useBoundStore } from '@/modules/shared/stores/store';
import {
  useThemeStore,
  useUpdateThemeMode,
} from '@/modules/shared/stores/useThemeStore';
import { ThemeWrapper, TooltipProvider } from '@AydiTeam1/aydi-ui';
import { ReactNode, useEffect } from 'react';
import { setFavicon } from '@/modules/shared/utils';

const FAVICON_LIGHT =
  'https://cdn.prod.website-files.com/68b6de6d1d27f7458deaf49f/68d5255f6d141934950b3410_favicon-light.svg';
const FAVICON_DARK =
  'https://cdn.prod.website-files.com/68b6de6d1d27f7458deaf49f/68d5255f165946e69a73cab4_favicon-dark.svg';

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const accessToken = useBoundStore((s) => s.accessToken);
  const { data } = useUserProfile(!!accessToken);
  const { mode, appTheme } = useThemeStore();
  const isRightToLeft = useIsRightToLeft();
  const applyThemeFromProfile = useUpdateThemeMode();

  const profileAppTheme = data?.userConfig?.appTheme;

  // Follow OS only when the user chose "System Default"
  useEffect(() => {
    if (appTheme !== 'System Default') return;

    const darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      useThemeStore
        .getState()
        .applyResolvedModeFromSystem(e.matches ? 'Dark' : 'Light');
    };

    darkMediaQuery.addEventListener('change', handleChange);
    return () => darkMediaQuery.removeEventListener('change', handleChange);
  }, [appTheme]);

  // Apply server preference when the profile loads or theme field changes
  useEffect(() => {
    if (!profileAppTheme) return;
    if (useThemeStore.getState().appTheme === profileAppTheme) return;
    applyThemeFromProfile(profileAppTheme);
  }, [profileAppTheme, applyThemeFromProfile]);

  // Ensure favicon reflects the resolved mode on every mount + change
  useEffect(() => {
    const resolved = mode ?? 'Light';
    setFavicon(resolved === 'Dark' ? FAVICON_DARK : FAVICON_LIGHT);
  }, [mode]);

  return (
    <ThemeWrapper mode={mode} dir={isRightToLeft ? 'rtl' : 'ltr'}>
      <TooltipProvider delayDuration={100}>{children}</TooltipProvider>
    </ThemeWrapper>
  );
};
